export const PENDING_SELECTION_KEY = 'wep_pending_warehouse_selection';

/**
 * @description
 * Returns true when both intent parameters are present and non-blank after trimming.
 * @param {string | null} environmentId - Installation identifier from the URL.
 * @param {string | null} warehouseCode - Warehouse code from the URL.
 * @returns {boolean} True if the intent is well-formed.
 */
export function isValidSelectionIntent(
  environmentId: string | null,
  warehouseCode: string | null
): boolean {
  return Boolean(environmentId?.trim()) && Boolean(warehouseCode?.trim());
}

/**
 * @description
 * Removes environmentId and warehouseCode from the real browser query string while
 * preserving pathname, other params, and hash. Needed because HashLocationStrategy
 * means Angular Router never sees or clears window.location.search.
 * @returns {void} This method does not return a value.
 */
function stripSelectionParamsFromBrowserUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('environmentId');
  url.searchParams.delete('warehouseCode');
  const search = url.searchParams.toString();
  const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

/**
 * @description
 * Parses a raw query string (e.g. window.location.search). When both environmentId
 * and warehouseCode are valid, persists them to sessionStorage and strips those
 * params from the browser URL so a reload does not re-capture the same intent.
 * @param {string} search - Raw search string, with or without a leading '?'.
 * @returns {void} This method does not return a value.
 */
export function captureSelectionIntentFromSearch(search: string): void {
  const params = new URLSearchParams(search);
  const environmentId = params.get('environmentId');
  const warehouseCode = params.get('warehouseCode');

  if (!isValidSelectionIntent(environmentId, warehouseCode)) {
    return;
  }

  sessionStorage.setItem(
    PENDING_SELECTION_KEY,
    JSON.stringify({ environmentId, warehouseCode })
  );
  stripSelectionParamsFromBrowserUrl();
}
